'use client';

import Icon from '@/components/ui/AppIcon';
import type { VisaMatrixAnalysisResponse } from '@/app/api/anthropic/visa-matrix-analysis/route';
import type { DestinationInput } from './VisaMatrixInteractive';
import Link from 'next/link';

interface ComparisonMatrixProps {
  data: VisaMatrixAnalysisResponse;
  destinations: DestinationInput[];
}

export default function ComparisonMatrix({ data, destinations }: ComparisonMatrixProps) {
  const getApprovalColor = (odds: number) => {
    if (odds >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (odds >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircleIcon" size={24} className="text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Easiest Destination</h3>
          </div>
          <p className="text-2xl font-black text-blue-600 mb-2">{data.summary.easiestDestination}</p>
          <p className="text-sm text-gray-600">{data.summary.easiestReason}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-yellow-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-yellow-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Most Complex</h3>
          </div>
          <p className="text-2xl font-black text-yellow-600 mb-2">{data.summary.mostComplexDestination}</p>
          <p className="text-sm text-gray-600">{data.summary.complexityReason}</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="LightBulbIcon" size={24} className="text-purple-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Recommendation</h3>
          </div>
          <p className="text-2xl font-black text-purple-600 mb-2">{data.summary.recommendedDestination}</p>
          <p className="text-sm text-gray-600">{data.summary.recommendationReason}</p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-black uppercase tracking-wider">
                  Criteria
                </th>
                {data.destinations.map((dest, idx) => (
                  <th key={idx} className="px-6 py-4 text-left text-sm font-black uppercase tracking-wider">
                    {dest.country}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Visa Required */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Visa Required</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4">
                    {dest.visaRequired ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-bold">
                        <Icon name="XCircleIcon" size={16} />
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-bold">
                        <Icon name="CheckCircleIcon" size={16} />
                        No
                      </span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Visa Type */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Visa Type</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4 text-sm text-gray-700">
                    {dest.visaType || 'N/A'}
                  </td>
                ))}
              </tr>

              {/* Processing Time */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Processing Time</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Icon name="ClockIcon" size={16} className="text-blue-600" />
                      <span className="text-sm font-bold text-gray-900">{dest.processingTime}</span>
                    </div>
                  </td>
                ))}
              </tr>

              {/* Approval Odds */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Approval Odds</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border-2 ${getApprovalColor(dest.approvalOdds)}`}>
                      <Icon name="ChartBarIcon" size={16} />
                      <span className="text-sm font-black">{dest.approvalOdds}%</span>
                    </div>
                  </td>
                ))}
              </tr>

              {/* Cost */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Visa Cost</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4 text-sm font-bold text-gray-900">
                    {dest.cost}
                  </td>
                ))}
              </tr>

              {/* Complexity */}
              <tr className="hover:bg-gray-50">
                <td className="px-6 py-4 font-bold text-gray-900">Complexity</td>
                {data.destinations.map((dest, idx) => (
                  <td key={idx} className="px-6 py-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold uppercase ${getComplexityColor(dest.complexity)}`}>
                      {dest.complexity}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Document Checklists */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.destinations.map((dest, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="DocumentTextIcon" size={24} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{dest.country}</h3>
            </div>
            <h4 className="text-sm font-bold text-gray-700 mb-3">Required Documents</h4>
            <ul className="space-y-2">
              {dest.requiredDocuments.map((doc, docIdx) => (
                <li key={docIdx} className="flex items-start gap-2 text-sm">
                  <Icon name="CheckIcon" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{doc}</span>
                </li>
              ))}
            </ul>
            {dest.additionalNotes && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <Icon name="InformationCircleIcon" size={14} className="inline mr-1" />
                  {dest.additionalNotes}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Key Insights */}
      {data.keyInsights.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-200">
          <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
            <Icon name="SparklesIcon" size={24} className="text-purple-600" />
            AI-Powered Key Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.keyInsights.map((insight, idx) => (
              <div key={idx} className="bg-white rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-700">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA to Onward Ticket */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
        <h3 className="text-2xl font-black mb-3">Ready to Book Your Onward Ticket?</h3>
        <p className="text-lg mb-6 opacity-90">
          Now that you know the visa requirements, secure your embassy-approved onward ticket
        </p>
        <Link
          href="/onward-ticket"
          className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-black rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
        >
          <Icon name="DocumentCheckIcon" size={24} />
          <span>Book Onward Ticket Now</span>
        </Link>
      </div>
    </div>
  );
}